import { type IconDefinition } from '@fortawesome/fontawesome-common-types'
import {
  faShieldSlash,
  faShieldXmark,
} from '@fortawesome/pro-regular-svg-icons'
import { type MantineColor, Skeleton } from '@mantine/core'
import { useRouter } from 'next/router'
import { type ReactNode } from 'react'

import { useQueryGetAllBookingInsurances } from '@flecto/core/saas'
import { useQueryGetUserLogged } from '@flecto/core/shared'
import { useTranslation } from '@flecto/translations'
import {
  type CoreInsurance,
  type CoreInsuranceStatus,
  type ParameterID,
} from '@flecto/types'
import { iconInsuranceOn } from '@flecto/ui'
import { isValidParameterID, formatDateString } from '@flecto/utils'

import { useInsuranceStatus } from '..'
import { useInsuranceManagerState } from '../../booking-sidebar/state'

type UseBookingInsuranceCardStatusOptions = {
  bookingId?: ParameterID
}

type InsuranceData = {
  [K in CoreInsuranceStatus]: {
    status: CoreInsuranceStatus
    insurances: CoreInsurance[]
    isLoading: boolean
    isUserInsured: boolean
    icon: {
      show: boolean
      icon: IconDefinition
      color: MantineColor
    }
    title: {
      show: boolean
      label: ReactNode
      color: MantineColor
    }
    badge: {
      show: boolean
      label: ReactNode
      color: MantineColor
    }
    action: {
      show: boolean
      label: ReactNode
      click: () => void
    }
  }
}

export function useBookingInsuranceCardStatus({
  bookingId,
}: UseBookingInsuranceCardStatusOptions): InsuranceData[CoreInsuranceStatus] {
  const { t } = useTranslation('saas', { keyPrefix: 'booking_insurances' })
  const { t: tActions } = useTranslation('shared', { keyPrefix: 'actions' })
  const { setModalOpened } = useInsuranceManagerState()

  const {
    data: queryList,
    isLoading: isLoadingInsurances,
    isFetching: isFetchingInsurances,
  } = useQueryGetAllBookingInsurances({ bookingId })

  const { data: user, isLoading: isLoadingUser } = useQueryGetUserLogged()

  const { data: insuranceStatus, isLoading: isLoadingInsuranceStatus } =
    useInsuranceStatus({ bookingId })

  const insuranceUntil = queryList?.until
  const isLoading =
    isLoadingInsurances ||
    isLoadingUser ||
    isFetchingInsurances ||
    isLoadingInsuranceStatus
  const insurances = queryList?.insurance ?? []
  const isUserInsured = user?.meta?.insured ?? false
  const router = useRouter()
  const isValidBooking = isValidParameterID(bookingId)

  const defaultItem: InsuranceData[CoreInsuranceStatus] = {
    icon: {
      icon: iconInsuranceOn,
      color: 'gray',
      show: true,
    },
    title: {
      show: true,
      label:
        insurances.length > 0
          ? t('insurance_issued_policies', { count: insurances.length })
          : t('insurance_no_issued_policies'),
      color: insurances.length > 0 ? 'gray.9' : 'gray.6',
    },
    badge: {
      show: false,
      label: '',
      color: 'gray',
    },
    action: {
      show: true,
      label: tActions('view'),
      click: () => setModalOpened(true),
    },
    status: 'ready',
    insurances,
    isLoading,
    isUserInsured,
  }

  if (!isUserInsured) {
    return {
      ...defaultItem,
      status: 'ready',
      isLoading: false,
      insurances: [],
      badge: {
        show: false,
        label: '',
        color: '',
      },
      action: {
        show: true,
        label: tActions('enable_integration'),
        click: () => router.push('/user/insurances'),
      },
    }
  }

  if (!isValidBooking) {
    return {
      ...defaultItem,
      status: 'ready',
      isLoading: false,
      title: {
        show: true,
        label: t('insurance_no_issued_policies'),
        color: 'dimmed',
      },
      badge: {
        show: true,
        label: t('insurance_ready'),
        color: 'gray',
      },
      action: {
        show: false,
        label: '',
        click: () => null,
      },
    }
  }

  if (isLoading) {
    return {
      ...defaultItem,
      status: 'ready',
      title: {
        show: true,
        label: <Skeleton width={150} height={14} mt='xs' radius='xl' />,
        color: 'gray.9',
      },
      badge: {
        show: true,
        label: <Skeleton width={100} height={20} mt='xs' radius='xl' />,
        color: 'gray',
      },
    }
  }

  const mapStatus: InsuranceData = {
    ready: {
      ...defaultItem,
      status: 'ready',
      badge: {
        show: true,
        label: t('insurance_ready'),
        color: 'gray',
      },
    },
    scheduled: {
      ...defaultItem,
      status: 'scheduled',
      badge: {
        show: true,
        label: t('insurance_scheduled'),
        color: 'gray',
      },
    },
    active: {
      ...defaultItem,
      status: 'active',
      icon: {
        show: true,
        icon: iconInsuranceOn,
        color: 'brand',
      },
      badge: {
        show: true,
        color: 'brand',
        label: insuranceUntil
          ? t('insurance_active', {
              replace: { date: formatDateString({ date: insuranceUntil }) },
            })
          : t('insurance_active_for_this_booking'),
      },
    },
    pending: {
      ...defaultItem,
      status: 'pending',
      isLoading: true,
      title: {
        show: true,
        label: t('insurance_issuing_policies'),
        color: 'gray.9',
      },
      badge: {
        show: true,
        label: t('insurance_ready'),
        color: 'gray',
      },
    },
    within_claim_period: {
      ...defaultItem,
      status: 'within_claim_period',
      icon: {
        show: true,
        icon: iconInsuranceOn,
        color: 'blue.6',
      },
      badge: {
        show: true,
        label: t('insurance_within_claim_period'),
        color: 'blue.6',
      },
    },
    expired: {
      ...defaultItem,
      status: 'expired',
      icon: {
        show: true,
        icon: faShieldSlash,
        color: 'gray',
      },
      badge: {
        show: true,
        label: t('insurance_expired'),
        color: 'gray',
      },
    },
    finished: {
      ...defaultItem,
      status: 'finished',
      icon: {
        show: true,
        icon: iconInsuranceOn,
        color: 'gray',
      },
      badge: {
        show: true,
        label: t('insurance_finished'),
        color: 'gray',
      },
    },
    canceled: {
      ...defaultItem,
      status: 'canceled',
      icon: {
        show: true,
        icon: faShieldXmark,
        color: 'red',
      },
      badge: {
        show: true,
        label: t('insurance_status_canceled'),
        color: 'red',
      },
    },
  }

  return mapStatus[(insuranceStatus as CoreInsuranceStatus) ?? 'ready']
}
